import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import {
  BodyText,
  Button,
  ListActionCollapsible,
  TextField,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import { CreditMenuItem } from '@order/components';
import { usePromoCode } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import type { ModalStackParamList } from '../../../../../navigation/AppNavigation.props';
import { useCheckoutContext } from '../../../CheckoutProvider';

export const ConnectedCheckoutPaymentBlockPromoCode = () => {
  const context = useCheckoutContext();

  return <CheckoutPaymentBlockPromoCode {...context} />;
};

export const CheckoutPaymentBlockPromoCode = (
  props: Pick<ReturnType<typeof useCheckoutContext>, 'refetchCredits'>,
) => {
  const { refetchCredits } = props;
  const {
    promoCode,
    isSubmittingPromoCode,
    promoCodeInvalid: hasPromoCodeError,
    isPromoCodeApplied,
    setPromoCode,
    submitPromoCode: onPromoCodeSubmit,
  } = usePromoCode(refetchCredits);

  const navigation =
    useNavigation<NativeStackNavigationProp<ModalStackParamList>>();
  const handlePressCreditAdd = useCallback(() => {
    navigation.navigate('CreditAdd');
  }, [navigation]);

  const shouldDisableApplyBtn = promoCode.trim() === '';

  const { currentBreakpoint } = useResponsive();
  const { t } = useLocalizationContext();

  if (currentBreakpoint.isXS)
    return (
      <CreditMenuItem
        testID="account.open-promo-code-selection-button"
        accessibilityLabel={t('account.credit.a11y-label')}
        accessibilityHint={t('account.credit.a11y-label')}
        iconName="IconPromo"
        title={t('account.credit.field-placeholder')}
        onPress={handlePressCreditAdd}
      />
    );

  const promoCodeAlreadyAppliedNotice = isPromoCodeApplied
    ? t('account.notice.credit-already-applied')
    : undefined;

  const invalidPromoCodeNotice = hasPromoCodeError
    ? t('account.notice.credit-invalid')
    : undefined;

  return (
    <ListActionCollapsible
      iconName="IconPromo"
      text={t('checkout.add-gift-card-code')}
    >
      <View>
        <View style={styles.promoCodeWrapper}>
          <View style={styles.promoCodeInputWrapper}>
            <TextField
              testID="checkout.promo-code-field"
              placeholder={t('checkout.add-gift-card-code')}
              clearButtonType="icon"
              value={promoCode}
              onChangeText={setPromoCode}
              clearBtnLabel={t('general.clear')}
              notice={promoCodeAlreadyAppliedNotice ?? invalidPromoCodeNotice}
              invalid={hasPromoCodeError}
              noticePalette="caution"
            />
          </View>

          <Button
            style={styles.applyButton}
            testID="checkout.promo-code-button"
            palette="primary"
            disabled={shouldDisableApplyBtn}
            isLoading={isSubmittingPromoCode}
            onPress={onPromoCodeSubmit}
          >
            {t('general.apply')}
          </Button>
        </View>

        <BodyText size={4}>{t('checkout.gift-card-note')}</BodyText>
      </View>
    </ListActionCollapsible>
  );
};

const styles = StyleSheet.create({
  promoCodeWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: theme.spacing['4'],
  },
  promoCodeInputWrapper: {
    flexGrow: 1,
    paddingRight: theme.spacing['8'],
    marginTop: theme.spacing['6'],
  },
  applyButton: {
    width: 88,
  },
});
